import { formatOfferPrice, isOfferInStock } from './productOffers';
import { isOfferExpired } from './offerStatus';

export const MAX_COMPARE_PRODUCTS = 4;
export const COMPARE_STORAGE_KEY = 'raheasy_compare';
export const COMPARE_CLEARED_FLAG = 'raheasy_compare_cleared';

export const markCompareCleared = () => {
  localStorage.setItem(COMPARE_CLEARED_FLAG, '1');
};

export const clearCompareClearedFlag = () => {
  localStorage.removeItem(COMPARE_CLEARED_FLAG);
};

export const isCompareExplicitlyCleared = () => {
  try {
    return localStorage.getItem(COMPARE_CLEARED_FLAG) === '1';
  } catch {
    return false;
  }
};

/** localStorage holds an intentional empty list (cleared or removed all items). */
export const isExplicitEmptyCompareList = () => {
  try {
    return localStorage.getItem(COMPARE_STORAGE_KEY) === '[]';
  } catch {
    return false;
  }
};

/** User chose an empty compare list — do not restore from the server on sync. */
export const shouldKeepCompareEmpty = () =>
  isCompareExplicitlyCleared() || isExplicitEmptyCompareList();

export const getCompareProductId = (product) =>
  String(product?.id || product?._id || '');

export const loadCompareList = () => {
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveCompareList = (items) => {
  localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(items));
};

const getProductImage = (product) => {
  if (!product) return '';
  if (product.thumbnail) return product.thumbnail;
  if (product.image) return product.image;
  if (Array.isArray(product.images) && product.images.length > 0) {
    const idx =
      product.primaryImageIndex != null &&
      product.primaryImageIndex < product.images.length
        ? product.primaryImageIndex
        : 0;
    return product.images[idx];
  }
  return '';
};

const normalizeSpecifications = (product) =>
  (Array.isArray(product?.specifications) ? product.specifications : [])
    .map((row) => ({
      key: String(row?.key || '').trim(),
      value: String(row?.value || '').trim()
    }))
    .filter((row) => row.key && row.value);

export const snapshotProductForCompare = (product) => {
  if (!product) return null;
  const id = getCompareProductId(product);
  if (!id) return null;

  return {
    id,
    _id: product._id || product.id,
    name: product.name,
    description: product.description || '',
    price: product.price,
    originalPrice: product.originalPrice,
    discount: product.discount,
    image: getProductImage(product),
    thumbnail: product.thumbnail,
    images: product.images,
    imageFallbacks: product.imageFallbacks,
    imagekitImages: product.imagekitImages,
    thumbnailFallback: product.thumbnailFallback,
    thumbnailImagekit: product.thumbnailImagekit,
    primaryImageIndex: product.primaryImageIndex,
    urlSlug: product.urlSlug,
    brand: product.brand,
    model: product.model,
    sku: product.sku,
    storeName: product.storeName,
    vendor: product.vendor,
    rating: product.rating,
    reviews: product.reviews,
    inStock: product.inStock,
    category: product.category != null ? String(product.category) : '',
    categoryName: product.categoryName,
    subCategoryName: product.subCategoryName,
    specifications: normalizeSpecifications(product),
    deliveryAvailable: product.deliveryAvailable,
    geek: product.geek,
    addedAt: Date.now(),
  };
};

export const addProductToCompare = (currentList, product) => {
  if (currentList.length >= MAX_COMPARE_PRODUCTS) return currentList;
  const snapshot = snapshotProductForCompare(product);
  if (!snapshot) return currentList;
  if (currentList.some((item) => getCompareProductId(item) === snapshot.id)) {
    return currentList;
  }
  return [...currentList, snapshot];
};

/** In-stock, non-expired store listings that can be added to compare. */
export const filterCompareEligibleOffers = (offers = []) =>
  (Array.isArray(offers) ? offers : []).filter(
    (offer) => offer && !isOfferExpired(offer) && isOfferInStock(offer)
  );

export const areAllOffersInCompare = (offers, compareProductIds) => {
  const eligible = filterCompareEligibleOffers(offers);
  if (!eligible.length) return false;
  return eligible.every((offer) => compareProductIds?.has(getCompareProductId(offer)));
};

export const countOffersNotInCompare = (offers, compareProductIds) =>
  filterCompareEligibleOffers(offers).filter(
    (offer) => !compareProductIds?.has(getCompareProductId(offer))
  ).length;

export const canAddOffersToCompare = (offers, compareProductIds, currentCompareCount = 0) => {
  const pending = countOffersNotInCompare(offers, compareProductIds);
  const slotsLeft = Math.max(0, MAX_COMPARE_PRODUCTS - (Number(currentCompareCount) || 0));
  return pending > 0 && slotsLeft > 0;
};

/** Add one or many products, respecting the compare limit and skipping duplicates. */
export const addProductsToCompare = (currentList, products = []) => {
  let next = currentList;
  let addedCount = 0;
  const list = Array.isArray(products) ? products : [products];

  list.forEach((product) => {
    if (next.length >= MAX_COMPARE_PRODUCTS) return;
    const before = next.length;
    next = addProductToCompare(next, product);
    if (next.length > before) addedCount += 1;
  });

  return { list: next, addedCount };
};

export const removeProductFromCompare = (currentList, productId) =>
  currentList.filter((item) => getCompareProductId(item) !== String(productId));

export const createCompareProductIdSet = (list) =>
  new Set(list.map((item) => getCompareProductId(item)).filter(Boolean));

export const isProductInCompare = (list, product) => {
  const id = getCompareProductId(product);
  if (!id) return false;
  return list.some((item) => getCompareProductId(item) === id);
};

export const mergeCompareLists = (primary = [], secondary = []) => {
  if (shouldKeepCompareEmpty()) return [];
  const merged = [];
  const seen = new Set();
  [...primary, ...secondary].forEach((item) => {
    const id = getCompareProductId(item);
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push(item);
  });
  return merged.slice(0, MAX_COMPARE_PRODUCTS);
};

export const buildCompareListFromIds = (ids = [], catalogProducts = []) => {
  const catalogById = new Map(
    (catalogProducts || []).map((p) => [getCompareProductId(p), p])
  );
  const items = [];
  ids.forEach((rawId) => {
    const id = String(rawId || '').trim();
    if (!id) return;
    const live = catalogById.get(id);
    const snapshot = live ? snapshotProductForCompare(live) : { id, _id: id };
    if (snapshot && !items.some((item) => getCompareProductId(item) === id)) {
      items.push(snapshot);
    }
  });
  return items.slice(0, MAX_COMPARE_PRODUCTS);
};

export const encodeCompareIdsParam = (products) =>
  products.map((product) => getCompareProductId(product)).filter(Boolean).join(',');

export const parseCompareIdsParam = (search = '') => {
  const params = new URLSearchParams(search);
  const raw = params.get('ids');
  if (!raw) return [];
  return raw
    .split(',')
    .map((id) => String(id || '').trim())
    .filter(Boolean)
    .slice(0, MAX_COMPARE_PRODUCTS);
};

/** Merge stored compare snapshots with live catalog rows when available. */
export const resolveCompareProducts = (compareItems, catalogProducts = []) => {
  const catalogById = new Map(
    (catalogProducts || []).map((p) => [getCompareProductId(p), p])
  );
  return compareItems.map((item) => {
    const id = getCompareProductId(item);
    const live = catalogById.get(id);
    if (!live) return item;
    return { ...item, ...snapshotProductForCompare(live), id, _id: live._id || live.id };
  });
};

export const getCompareCategoryKey = (product) => {
  const category = String(product?.category || '').trim();
  if (category && category !== 'undefined') return category;
  const name = String(product?.categoryName || '').trim().toLowerCase();
  return name || '';
};

export const hasMixedCompareCategories = (products = []) => {
  const keys = new Set(
    products.map(getCompareCategoryKey).filter(Boolean)
  );
  return keys.size > 1;
};

const countFilledSpecifications = (product) =>
  (product?.specifications || []).filter((row) => row?.key && row?.value).length;

export const computeMatchScore = (product) => {
  let score = (Number(product.rating) || 0) * 16;
  if (product.inStock !== false) score += 14;
  if (product.discount > 0) score += Math.min(product.discount / 4, 12);
  if (product.reviews > 0) score += Math.min(Math.log10(product.reviews + 1) * 6, 10);
  if (product.deliveryAvailable) score += 4;
  score += Math.min(countFilledSpecifications(product) * 3, 15);
  return Math.round(Math.min(100, Math.max(0, score)));
};

export const getTopMatchProductId = (products) => {
  if (!products.length) return null;
  let bestId = getCompareProductId(products[0]);
  let bestScore = computeMatchScore(products[0]);
  products.forEach((product) => {
    const score = computeMatchScore(product);
    if (score > bestScore) {
      bestScore = score;
      bestId = getCompareProductId(product);
    }
  });
  return bestId;
};

export const getLowestPriceProductId = (products) => {
  const inStock = products.filter((p) => p.inStock !== false);
  const pool = inStock.length ? inStock : products;
  if (!pool.length) return null;
  let bestId = getCompareProductId(pool[0]);
  let bestPrice = Number(pool[0].price) || Infinity;
  pool.forEach((product) => {
    const price = Number(product.price) || Infinity;
    if (price < bestPrice) {
      bestPrice = price;
      bestId = getCompareProductId(product);
    }
  });
  return bestId;
};

const collectSpecificationKeys = (products) => {
  const keys = new Set();
  products.forEach((product) => {
    (product.specifications || []).forEach((row) => {
      if (row?.key) keys.add(row.key);
    });
  });
  return [...keys];
};

const buildSpecificationsSection = (products) => {
  const keys = collectSpecificationKeys(products);
  if (!keys.length) return null;

  const rows = keys.map((key) => {
    const values = products.map((product) => {
      const match = (product.specifications || []).find((row) => row.key === key);
      return match?.value ? String(match.value) : '—';
    });
    return {
      label: key,
      values,
      allSame: values.every((value) => value === values[0])
    };
  });

  return {
    id: 'specifications',
    title: 'Specifications',
    rows
  };
};

export const buildCompareSpecSections = (products) => {
  const rows = (entries) =>
    entries
      .map(([label, getValue]) => {
        const values = products.map((p) => getValue(p));
        const normalized = values.map((v) => String(v ?? '—'));
        const allSame = normalized.every((v) => v === normalized[0]);
        return { label, values: normalized, allSame };
      })
      .filter((row) => row.values.some((v) => v && v !== '—'));

  const sections = [
    {
      id: 'overview',
      title: 'Overview',
      rows: rows([
        ['Brand', (p) => p.brand || '—'],
        ['Model', (p) => p.model || '—'],
        ['SKU', (p) => p.sku || '—'],
        ['Store', (p) => p.storeName || p.vendor || '—'],
        ['Category', (p) => p.categoryName || '—'],
        ['Subcategory', (p) => p.subCategoryName || '—'],
      ]),
    },
    {
      id: 'pricing',
      title: 'Pricing',
      rows: rows([
        ['Price', (p) => formatOfferPrice(p.price)],
        ['Original price', (p) => (p.originalPrice ? formatOfferPrice(p.originalPrice) : '—')],
        ['Discount', (p) => (p.discount > 0 ? `${p.discount}%` : '—')],
      ]),
    },
  ].filter((section) => section.rows.length > 0);

  const customSection = buildSpecificationsSection(products);
  if (customSection) sections.push(customSection);

  return sections;
};
